// ─────────────────────────────────────────────────────────────────────────────
// The ocean's sound effects, made with Web Audio (no files to load): getting
// bitten, landing a hit on a beast, beating one, and losing all your lives.
// Students can turn them off (🔊 on the ocean page; remembered in this browser).
// ─────────────────────────────────────────────────────────────────────────────

let ctx: AudioContext | null = null;
let muted = (() => { try { return localStorage.getItem('ocean.sfx') === 'off'; } catch { return false; } })();

export const sfxMuted = () => muted;
export function setSfxMuted(m: boolean) {
  muted = m;
  try { localStorage.setItem('ocean.sfx', m ? 'off' : 'on'); } catch { /* fine */ }
  if (m) stopAmbience();
}

function audio(): AudioContext | null {
  if (muted) return null;
  try {
    ctx ??= new AudioContext();
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  } catch { return null; }
}

/** One voice: a pitch sweep with a quick attack and a fade. */
function voice(a: AudioContext, type: OscillatorType, from: number, to: number, start: number, dur: number, vol: number) {
  const o = a.createOscillator(), g = a.createGain();
  o.type = type;
  o.frequency.setValueAtTime(from, start);
  o.frequency.exponentialRampToValueAtTime(Math.max(20, to), start + dur);
  g.gain.setValueAtTime(0.0001, start);
  g.gain.exponentialRampToValueAtTime(vol, start + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  o.connect(g).connect(a.destination);
  o.start(start);
  o.stop(start + dur + 0.02);
}

/** A short burst of noise (a splash or a thump), low-passed. */
function noise(a: AudioContext, start: number, dur: number, vol: number, cutoff: number) {
  const len = Math.max(1, Math.floor(a.sampleRate * dur));
  const buf = a.createBuffer(1, len, a.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / len);
  const src = a.createBufferSource(), f = a.createBiquadFilter(), g = a.createGain();
  src.buffer = buf; f.type = 'lowpass'; f.frequency.value = cutoff;
  g.gain.value = vol;
  src.connect(f).connect(g).connect(a.destination);
  src.start(start);
}

/** Bitten or stung: a thump and a falling "ow". */
export function sfxHurt() {
  const a = audio(); if (!a) return;
  const t = a.currentTime;
  noise(a, t, 0.18, 0.5, 700);
  voice(a, 'square', 520, 180, t, 0.28, 0.09);
  voice(a, 'sine', 140, 60, t, 0.22, 0.25);
}

/** A sword or trident lands on a beast. */
export function sfxHit() {
  const a = audio(); if (!a) return;
  const t = a.currentTime;
  noise(a, t, 0.08, 0.35, 2400);
  voice(a, 'triangle', 900, 300, t, 0.12, 0.12);
}

/** A beast is beaten: a rising sparkle. */
export function sfxDefeat() {
  const a = audio(); if (!a) return;
  const t = a.currentTime;
  [523, 659, 784, 1047].forEach((f, i) => voice(a, 'triangle', f, f * 1.01, t + i * 0.07, 0.18, 0.1));
}

/** All three lives gone: a long sinking wail. */
export function sfxFaint() {
  const a = audio(); if (!a) return;
  const t = a.currentTime;
  voice(a, 'sawtooth', 440, 70, t, 0.9, 0.07);
  voice(a, 'sine', 220, 40, t + 0.05, 0.9, 0.18);
  noise(a, t, 0.4, 0.3, 500);
}

/** An epic or legendary fish nearby: a shimmering arpeggio. */
export function sfxRare() {
  const a = audio(); if (!a) return;
  const t = a.currentTime;
  [988, 1319, 1568, 1976, 2637].forEach((f, i) => voice(a, 'sine', f, f, t + i * 0.06, 0.4, 0.06));
}

// ── The shop and the arcade ─────────────────────────────────────────────────

/** Sand dollars spent or earned: a bright double clink. */
export function sfxCoin() {
  const a = audio(); if (!a) return;
  const t = a.currentTime;
  voice(a, 'square', 1319, 1319, t, 0.07, 0.05);
  voice(a, 'square', 1976, 1976, t + 0.07, 0.18, 0.05);
}

/** An egg wobbling and cracking. */
export function sfxCrack() {
  const a = audio(); if (!a) return;
  const t = a.currentTime;
  noise(a, t, 0.05, 0.5, 3500);
  voice(a, 'triangle', 300, 180, t, 0.08, 0.12);
}

/** A right answer (rising) or a wrong one (falling). */
export function sfxRight() {
  const a = audio(); if (!a) return;
  const t = a.currentTime;
  [659, 784, 1047].forEach((f, i) => voice(a, 'triangle', f, f, t + i * 0.08, 0.14, 0.1));
}
export function sfxWrong() {
  const a = audio(); if (!a) return;
  const t = a.currentTime;
  voice(a, 'square', 220, 150, t, 0.3, 0.06);
}

/** A hop. */
export function sfxJump() {
  const a = audio(); if (!a) return;
  voice(a, 'square', 300, 620, a.currentTime, 0.12, 0.05);
}

/** A spring, a stomp, a door or a checkpoint: a springy boing. */
export function sfxBoing() {
  const a = audio(); if (!a) return;
  const t = a.currentTime;
  voice(a, 'triangle', 220, 880, t, 0.18, 0.12);
  voice(a, 'sine', 440, 660, t + 0.05, 0.15, 0.06);
}

/** A wet little splat: a squishy fish landing from a hop. Soft, and a touch different each time. */
export function sfxSplat() {
  const a = audio(); if (!a) return;
  const t = a.currentTime, k = 0.85 + Math.random() * 0.3;
  noise(a, t, 0.07, 0.22, 900 * k);
  voice(a, 'sine', 260 * k, 70, t, 0.1, 0.12);
}

/** A bowstring: an arrow let loose. */
export function sfxBow() {
  const a = audio(); if (!a) return;
  const t = a.currentTime;
  voice(a, 'triangle', 720, 260, t, 0.12, 0.08);
  noise(a, t, 0.05, 0.12, 2500);
}

/** A cannon's boom (low) or a robot's laser (high). */
export function sfxShoot(kind: 'ball' | 'laser') {
  const a = audio(); if (!a) return;
  const t = a.currentTime;
  if (kind === 'ball') { noise(a, t, 0.18, 0.35, 600); voice(a, 'sine', 160, 50, t, 0.25, 0.15); }
  else voice(a, 'square', 1400, 300, t, 0.15, 0.04);
}

// ── Ambience: a quiet bed that changes with where you are ───────────────────
// A low rumble of filtered noise and a soft drone, plus little sounds now and
// then: bubbles near the surface, creaking wood at the wrecks, drips in the
// caves, slow chimes in the temples, and a heartbeat when the Kraken is near.

export type Mood = 'surface' | 'wreck' | 'deep' | 'cave' | 'temple' | 'boss';

const MOODS: Record<Mood, { cutoff: number; bed: number; drone: [number, number]; droneVol: number; every: number }> = {
  surface: { cutoff: 900, bed: 0.5, drone: [110, 165], droneVol: 0.04, every: 1.2 },
  wreck: { cutoff: 600, bed: 0.55, drone: [98, 147], droneVol: 0.05, every: 2.6 },
  deep: { cutoff: 380, bed: 0.65, drone: [73, 110], droneVol: 0.07, every: 3 },
  cave: { cutoff: 420, bed: 0.5, drone: [82, 123], droneVol: 0.06, every: 1.8 },
  temple: { cutoff: 340, bed: 0.55, drone: [65, 98], droneVol: 0.09, every: 3.5 },
  boss: { cutoff: 300, bed: 0.7, drone: [55, 58], droneVol: 0.12, every: 0.9 },
};

let amb: { master: GainNode; filter: BiquadFilterNode; bed: GainNode; drones: OscillatorNode[]; droneGain: GainNode; timer: number; mood: Mood; night: number } | null = null;

export function startAmbience() {
  const a = audio();
  if (!a || amb) return;
  const master = a.createGain(); master.gain.value = 0; master.connect(a.destination);
  master.gain.linearRampToValueAtTime(0.5, a.currentTime + 2);
  // Brown noise, looped: the sea's rumble.
  const len = a.sampleRate * 4, buf = a.createBuffer(1, len, a.sampleRate), d = buf.getChannelData(0);
  let last = 0;
  for (let i = 0; i < len; i++) { last = (last + 0.02 * (Math.random() * 2 - 1)) / 1.02; d[i] = last * 3.5; }
  const src = a.createBufferSource(); src.buffer = buf; src.loop = true;
  const filter = a.createBiquadFilter(); filter.type = 'lowpass'; filter.frequency.value = 900;
  const bed = a.createGain(); bed.gain.value = 0.5;
  src.connect(filter).connect(bed).connect(master); src.start();
  const droneGain = a.createGain(); droneGain.gain.value = 0.04; droneGain.connect(master);
  const drones = [110, 165].map(f => { const o = a.createOscillator(); o.type = 'sine'; o.frequency.value = f; o.connect(droneGain); o.start(); return o; });
  amb = { master, filter, bed, drones, droneGain, timer: 0, mood: 'surface', night: 0 };
  let next = 0;
  amb.timer = window.setInterval(() => {
    const c = amb; if (!c || muted || !ctx) return;
    const now = ctx.currentTime;
    if (now < next) return;
    const m = MOODS[c.mood];
    next = now + m.every * (c.mood === 'boss' ? 1 : 0.6 + Math.random() * 0.8);
    detail(ctx, c.mood, c.master);
  }, 150);
}

/** Where you are now; the sound eases over in a couple of seconds. */
export function setMood(mood: Mood, night: number) {
  const a = ctx;
  if (!amb || !a) return;
  if (amb.mood === mood && Math.abs(amb.night - night) < 0.05) return;
  amb.mood = mood; amb.night = night;
  const m = MOODS[mood], t = a.currentTime;
  amb.filter.frequency.setTargetAtTime(m.cutoff * (1 - night * 0.35), t, 0.8);
  amb.bed.gain.setTargetAtTime(m.bed, t, 0.8);
  amb.droneGain.gain.setTargetAtTime(m.droneVol, t, 1);
  amb.drones.forEach((o, i) => o.frequency.setTargetAtTime(m.drone[i], t, 1.2));
}

export function stopAmbience() {
  if (!amb) return;
  const c = amb; amb = null;
  window.clearInterval(c.timer);
  try {
    c.master.gain.setTargetAtTime(0, ctx!.currentTime, 0.3);
    window.setTimeout(() => { c.drones.forEach(o => o.stop()); c.master.disconnect(); }, 1500);
  } catch { /* fine */ }
}

/** The little sounds of each place, routed through the ambience's volume. */
function detail(a: AudioContext, mood: Mood, out: AudioNode) {
  const t = a.currentTime;
  const ping = (f0: number, f1: number, dur: number, vol: number, type: OscillatorType = 'sine', at = 0) => {
    const o = a.createOscillator(), g = a.createGain();
    o.type = type; o.frequency.setValueAtTime(f0, t + at); o.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t + at + dur);
    g.gain.setValueAtTime(0.0001, t + at); g.gain.exponentialRampToValueAtTime(vol, t + at + 0.01); g.gain.exponentialRampToValueAtTime(0.0001, t + at + dur);
    o.connect(g).connect(out); o.start(t + at); o.stop(t + at + dur + 0.05);
  };
  if (mood === 'surface') {
    // A few bubbles rising.
    const n = 2 + Math.floor(Math.random() * 3);
    for (let i = 0; i < n; i++) { const f = 500 + Math.random() * 600; ping(f, f * 2.2, 0.08, 0.12, 'sine', i * 0.07); }
  } else if (mood === 'wreck') {
    // Old wood creaking.
    const o = a.createOscillator(), f = a.createBiquadFilter(), g = a.createGain();
    o.type = 'sawtooth'; f.type = 'bandpass'; f.frequency.value = 420; f.Q.value = 8;
    const base = 70 + Math.random() * 40;
    o.frequency.setValueAtTime(base, t); o.frequency.linearRampToValueAtTime(base * 1.4, t + 0.6); o.frequency.linearRampToValueAtTime(base * 0.9, t + 1.1);
    g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.18, t + 0.15); g.gain.exponentialRampToValueAtTime(0.0001, t + 1.1);
    o.connect(f).connect(g).connect(out); o.start(t); o.stop(t + 1.2);
  } else if (mood === 'cave') {
    // A drip echoing.
    const f = 1200 + Math.random() * 900;
    ping(f, f * 0.6, 0.18, 0.16); ping(f * 0.98, f * 0.58, 0.2, 0.05, 'sine', 0.22);
  } else if (mood === 'deep') {
    // Something big, far off.
    ping(90, 60, 1.6, 0.12, 'sine');
  } else if (mood === 'temple') {
    // A slow, minor chime.
    const notes = [220, 261.6, 329.6, 207.7];
    const f = notes[Math.floor(Math.random() * notes.length)];
    ping(f, f, 2.2, 0.07, 'triangle'); ping(f * 2, f * 2, 1.6, 0.03, 'sine', 0.02);
  } else {
    // The Kraken is near: a heartbeat.
    ping(70, 45, 0.16, 0.5, 'sine'); ping(62, 40, 0.16, 0.4, 'sine', 0.24);
  }
}
